import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _extends from '@babel/runtime/helpers/esm/extends';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import propTypes from 'prop-types';
import requiredIf from 'react-required-if';
import { Stack, SkeletonText, Flex, HStack, Box, Grid, GridItem, Wrap, WrapItem, Link, RadioGroup, Radio } from '@chakra-ui/react';
import { Text } from '@outdoorly/text';
import { Divider } from '@outdoorly/divider';
import { Card } from '@outdoorly/card';
import { SummaryTable } from '@outdoorly/summary-table';
import { AmexIcon, DinersClubIcon, DiscoverIcon, JcbIcon, MastercardIcon, VisaIcon } from '@outdoorly/icons';
import NextLink from 'next/link';
import { Cluster } from '@outdoorly/cluster';
import { ProductSummaryContainer } from '@outdoorly/product-summary';
import { usePricing } from '@outdoorly/util';

var _excluded = ["shippingAddress", "paymentMethod", "summary", "currency"];
var AddressDetails = function AddressDetails(_ref) {
  var firstName = _ref.firstName,
      lastName = _ref.lastName,
      streetAddress1 = _ref.streetAddress1,
      streetAddress2 = _ref.streetAddress2,
      country = _ref.country,
      city = _ref.city,
      countryArea = _ref.countryArea,
      postalCode = _ref.postalCode,
      phone = _ref.phone,
      _ref$unknown = _ref.unknown,
      unknown = _ref$unknown === void 0 ? false : _ref$unknown;
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 3
  }, unknown ? /*#__PURE__*/React.createElement(SkeletonText, {
    noOfLines: 2,
    skeletonHeight: "1rem",
    spacing: "2",
    startColor: "scheme.border",
    endColor: "scheme.border"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, null, ["".concat(firstName, " ").concat(lastName), streetAddress1, streetAddress2].filter(Boolean).join(', ')), /*#__PURE__*/React.createElement(Text, {
    color: "scheme.textSecondary"
  }, [city, countryArea, postalCode, typeof country === 'string' ? country : country === null || country === void 0 ? void 0 : country.country].filter(Boolean).join(', ')), phone && /*#__PURE__*/React.createElement(Text, {
    color: "scheme.textSecondary"
  }, phone)));
};
AddressDetails.propTypes = {
  firstName: propTypes.string,
  lastName: propTypes.string,
  streetAddress1: propTypes.string,
  streetAddress2: propTypes.string,
  country: propTypes.shape({
    country: propTypes.string,
    code: propTypes.string
  }),
  city: propTypes.string,
  countryArea: propTypes.string,
  postalCode: propTypes.string,
  phone: propTypes.string,

  /**
   * If set, will display solid gray skeleton lines instead of actual text
   */
  unknown: propTypes.bool
};
var OrderAddress = function OrderAddress(_ref2) {
  var address = _extends({}, _ref2);

  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 4
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: "row",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500",
    size: "lg"
  }, "Shipping address")), /*#__PURE__*/React.createElement(AddressDetails, address));
};
OrderAddress.propTypes = _objectSpread({
  /**
   * When present, a `Change` option will be visible
   */
  onChangeClick: propTypes.func,

  /**
   * Uses a compact display mode
   */
  compact: propTypes.bool
}, AddressDetails.propTypes);

var PaymentMethodIcon = function PaymentMethodIcon(_ref3) {
  var _iconMapping$brand;

  var brand = _ref3.brand;

  var defaultIcon = function defaultIcon() {
    return /*#__PURE__*/React.createElement(Card, {
      p: 2,
      borderRadius: "md",
      bg: "scheme.controlDisabled",
      color: "scheme.textPrimaryInverse"
    }, /*#__PURE__*/React.createElement(Text, {
      size: "sm",
      fontWeight: 500
    }, brand.toUpperCase()));
  };

  var iconMapping = {
    amex: AmexIcon,
    diners: DinersClubIcon,
    discover: DiscoverIcon,
    jcb: JcbIcon,
    mastercard: MastercardIcon,
    visa: VisaIcon
  };
  var ProviderIcon = (_iconMapping$brand = iconMapping[brand]) !== null && _iconMapping$brand !== void 0 ? _iconMapping$brand : defaultIcon;
  return /*#__PURE__*/React.createElement(ProviderIcon, {
    size: "32px"
  });
};

PaymentMethodIcon.propTypes = {
  brand: propTypes.string.isRequired
};
var PaymentMethodDetails = function PaymentMethodDetails(_ref4) {
  var name = _ref4.name,
      brand = _ref4.brand,
      lastDigits = _ref4.lastDigits,
      expMonth = _ref4.expMonth,
      expYear = _ref4.expYear,
      _ref4$unknown = _ref4.unknown,
      unknown = _ref4$unknown === void 0 ? false : _ref4$unknown;
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 3
  }, unknown ? /*#__PURE__*/React.createElement(SkeletonText, {
    noOfLines: 1,
    skeletonHeight: "1rem",
    spacing: "2",
    startColor: "scheme.border",
    endColor: "scheme.border"
  }) : /*#__PURE__*/React.createElement(Stack, {
    align: "center",
    direction: "row"
  }, /*#__PURE__*/React.createElement(PaymentMethodIcon, {
    brand: brand
  }), /*#__PURE__*/React.createElement(HStack, {
    minWidth: "3em"
  }, /*#__PURE__*/React.createElement(Text, null, "\xB7\xB7\xB7\xA0", lastDigits), /*#__PURE__*/React.createElement(Text, {
    color: "scheme.textSecondary"
  }, "".concat(expMonth, "/").concat(expYear)), /*#__PURE__*/React.createElement(Text, {
    as: "div",
    minWidth: "2em"
  }, /*#__PURE__*/React.createElement(Box, {
    as: "p",
    isTruncated: true
  }, name)))));
};
PaymentMethodDetails.propTypes = {
  // Full name of person on the card
  name: requiredIf(propTypes.string, function (_ref5) {
    var unknown = _ref5.unknown;
    return !unknown;
  }),
  provider: requiredIf(propTypes.oneOf(['visa', 'mastercard', 'amex', 'diners', 'discover', 'jcb']), function (_ref6) {
    var unknown = _ref6.unknown;
    return !unknown;
  }),
  // Last 4 digits of the credit card
  lastDigits: requiredIf(propTypes.string, function (_ref7) {
    var unknown = _ref7.unknown;
    return !unknown;
  }),
  // Are these details unknown?
  unknown: propTypes.bool
};
var OrderPaymentMethod = function OrderPaymentMethod(_ref8) {
  var paymentMethod = _extends({}, _ref8);

  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 4
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500",
    size: "lg"
  }, "Payment method")), /*#__PURE__*/React.createElement(PaymentMethodDetails, paymentMethod));
};
OrderPaymentMethod.propTypes = _objectSpread({
  /**
   * When present, a `Change` option will be visible
   */
  onChangeClick: propTypes.func,

  /**
   * Uses a compact display mode
   */
  compact: propTypes.bool
}, PaymentMethodDetails.propTypes); // FIXME: I18n. Upgrade to node >=13.x to add other locale's currencies. By
// default, it will fall back to `en-us` for _everything_ in <=12.x. Depends
// on Vercel supporting >12.x, which depends on AWS supporting >12.x. See:
// - https://github.com/nodejs/node/issues/19214#issuecomment-547651887
// - https://vercel.com/docs/runtimes#official-runtimes/node-js/node-js-version
// - https://docs.aws.amazon.com/lambda/latest/dg/lambda-runtimes.html

var currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});
var ProductLine = function ProductLine(_ref9) {
  var _line$variant$thumbna, _line$variant$thumbna2, _line$variant$product, _line$variant, _line$variant$product2;

  var line = _ref9.line,
      children = _ref9.children;

  var _usePricing = usePricing(line.variant),
      discountedPrice = _usePricing.discountedPrice,
      undiscountedPrice = _usePricing.undiscountedPrice;

  return /*#__PURE__*/React.createElement(ProductSummaryContainer, {
    key: line.id,
    src: (_line$variant$thumbna = (_line$variant$thumbna2 = line.variant.thumbnail) === null || _line$variant$thumbna2 === void 0 ? void 0 : _line$variant$thumbna2.url) !== null && _line$variant$thumbna !== void 0 ? _line$variant$thumbna : (_line$variant$product = line.variant.product.thumbnail) === null || _line$variant$product === void 0 ? void 0 : _line$variant$product.url,
    size: 20
  }, /*#__PURE__*/React.createElement(Grid, {
    templateColumns: "repeat(5,1fr)"
  }, /*#__PURE__*/React.createElement(GridItem, {
    colSpan: 4
  }, /*#__PURE__*/React.createElement(Wrap, {
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(WrapItem, {
    flex: 2,
    minW: 40
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-start"
  }, /*#__PURE__*/React.createElement(NextLink, {
    href: "/products/".concat((_line$variant = line.variant) === null || _line$variant === void 0 ? void 0 : (_line$variant$product2 = _line$variant.product) === null || _line$variant$product2 === void 0 ? void 0 : _line$variant$product2.slug),
    passHref: true
  }, /*#__PURE__*/React.createElement(Link, {
    fontWeight: "500",
    leading: "base"
  }, line.variant.product.name)), /*#__PURE__*/React.createElement(Text, {
    size: "md",
    color: "scheme.textSecondary",
    leading: "short",
    sx: {
      paddingBottom: '7px',
      maxWidth: '120px'
    }
  }, line.humanFriendlyVariantName || ''), /*#__PURE__*/React.createElement(HStack, null, /*#__PURE__*/React.createElement(Text, {
    color: "scheme.priceRegular",
    textDecoration: discountedPrice ? 'line-through' : 'none'
  }, undiscountedPrice), discountedPrice && /*#__PURE__*/React.createElement(Text, {
    color: "scheme.priceDiscounted"
  }, discountedPrice)))), /*#__PURE__*/React.createElement(WrapItem, {
    flex: 1,
    alignItems: "center"
  }, children))), /*#__PURE__*/React.createElement(GridItem, {
    as: Flex,
    colSpan: 1,
    justify: "end",
    align: "center"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
    color: "scheme.textSecondary"
  }, currencyFormatter.format(line.totalPrice.net.amount))))));
};
ProductLine.propTypes = {
  children: propTypes.any,
  line: propTypes.shape({
    humanFriendlyVariantName: propTypes.any,
    id: propTypes.any,
    totalPrice: propTypes.shape({
      net: propTypes.shape({
        amount: propTypes.any
      })
    }),
    variant: propTypes.shape({
      attributes: propTypes.arrayOf(propTypes.shape()),
      thumbnail: propTypes.shape({
        url: propTypes.string
      }),
      product: propTypes.shape({
        thumbnail: propTypes.shape({
          url: propTypes.any
        }),
        name: propTypes.any,
        slug: propTypes.string
      })
    })
  })
};
var OrderSummaryTable = function OrderSummaryTable(_ref10) {
  var summary = _ref10.summary,
      includesShipping = _ref10.includesShipping,
      currency = _ref10.currency;
  var currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency !== null && currency !== void 0 ? currency : 'USD'
  });
  return /*#__PURE__*/React.createElement(SummaryTable, {
    highlightLast: true
    /* eslint-disable react/jsx-key */
    ,
    data: [summary.items != null && ['Items:', currencyFormatter.format(summary.items)], summary.shipping != null && ['Shipping:', includesShipping ? currencyFormatter.format(summary.shipping) : /*#__PURE__*/React.createElement(SkeletonText, {
      noOfLines: 1,
      skeletonHeight: "1rem",
      spacing: "2",
      startColor: "scheme.border",
      endColor: "scheme.border"
    })], summary.tax != null && ['Tax:', includesShipping ? currencyFormatter.format(summary.tax) : /*#__PURE__*/React.createElement(SkeletonText, {
      noOfLines: 1,
      skeletonHeight: "1rem",
      spacing: "2",
      startColor: "scheme.border",
      endColor: "scheme.border"
    })], summary.credit != null && [/*#__PURE__*/React.createElement(Box, {
      as: "span",
      color: "scheme.priceDiscounted"
    }, "Credit:"), /*#__PURE__*/React.createElement(Box, {
      as: "span",
      color: "scheme.priceDiscounted"
    }, currencyFormatter.format(summary.credit))], summary.promotionalDiscount != null && [/*#__PURE__*/React.createElement(Box, {
      as: "span",
      color: "scheme.priceDiscounted"
    }, "Promo:"), /*#__PURE__*/React.createElement(Box, {
      as: "span",
      color: "scheme.priceDiscounted"
    }, currencyFormatter.format(summary.promotionalDiscount))], summary.totalDiscount != null && [/*#__PURE__*/React.createElement(Box, {
      as: "span",
      color: "scheme.priceDiscounted"
    }, "Total Savings:"), /*#__PURE__*/React.createElement(Box, {
      as: "span",
      color: "scheme.priceDiscounted"
    }, currencyFormatter.format(summary.totalDiscount))], ['Order Total:', includesShipping ? currencyFormatter.format(summary.total) : /*#__PURE__*/React.createElement(SkeletonText, {
      noOfLines: 1,
      skeletonHeight: "1rem",
      spacing: "2",
      startColor: "scheme.border",
      endColor: "scheme.border"
    })]].filter(Boolean)
    /* eslint-enable react/jsx-key */

  });
};
OrderSummaryTable.propTypes = {
  includesShipping: propTypes.bool,
  summary: propTypes.shape({
    items: propTypes.number,
    shipping: propTypes.number,
    tax: propTypes.number,
    promotionalDiscount: propTypes.number,
    totalDiscount: propTypes.number,
    total: propTypes.number.isRequired,
    credit: propTypes.number
  }).isRequired,
  currency: propTypes.string
};
var OrderSummary = function OrderSummary(_ref11) {
  var shippingAddress = _ref11.shippingAddress,
      paymentMethod = _ref11.paymentMethod,
      summary = _ref11.summary,
      currency = _ref11.currency,
      props = _objectWithoutProperties(_ref11, _excluded);

  return /*#__PURE__*/React.createElement(Stack, _extends({
    spacing: 8
  }, props), /*#__PURE__*/React.createElement(OrderAddress, _extends({}, shippingAddress, {
    unknown: !shippingAddress
  })), /*#__PURE__*/React.createElement(OrderPaymentMethod, _extends({}, paymentMethod, {
    unknown: !paymentMethod
  })), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(OrderSummaryTable, {
    includesShipping: !!shippingAddress,
    summary: summary,
    currency: currency
  }));
};
OrderSummary.propTypes = {
  shippingAddress: AddressDetails.propTypes,
  onChangeAddressClick: propTypes.func,
  paymentMethod: PaymentMethodDetails.propTypes,
  onChangePaymentMethodClick: propTypes.func,
  summary: OrderSummaryTable.propTypes.summary,
  currency: propTypes.string
};
var BrandCheckout = function BrandCheckout(_ref12) {
  var brandCheckout = _ref12.brandCheckout;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Cluster, {
    gapX: 2
  }, /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500"
  }, brandCheckout.brand.name), /*#__PURE__*/React.createElement(Text, {
    color: "scheme.textSecondary"
  }, "".concat(brandCheckout.quantity, " ").concat(brandCheckout.quantity === 1 ? 'item' : 'items')))), brandCheckout.lines.map(function (line) {
    return /*#__PURE__*/React.createElement(ProductLine, {
      key: line.id,
      line: line
    }, /*#__PURE__*/React.createElement(Text, {
      size: "md",
      color: "scheme.textSecondary"
    }, "Qty: ", line.quantity));
  }));
};
BrandCheckout.propTypes = {
  brandCheckout: propTypes.shape({
    brand: propTypes.shape({
      name: propTypes.string
    }),
    lines: propTypes.arrayOf(propTypes.shape({
      id: propTypes.string,
      quantity: propTypes.number
    })),
    quantity: propTypes.number
  })
};
var BrandCheckoutShippingMethod = function BrandCheckoutShippingMethod(_ref13) {
  var _brandCheckout$availa2, _brandCheckout$brand$, _brandCheckout$brand;

  var brandCheckout = _ref13.brandCheckout,
      _ref13$readyToSelect = _ref13.readyToSelect,
      readyToSelect = _ref13$readyToSelect === void 0 ? false : _ref13$readyToSelect,
      onSelectMethod = _ref13.onSelectMethod,
      selectedMethod = _ref13.selectedMethod;

  var _useState = useState(selectedMethod !== null && selectedMethod !== void 0 ? selectedMethod : ''),
      _useState2 = _slicedToArray(_useState, 2),
      internalSelection = _useState2[0],
      setInternalSelection = _useState2[1];

  useEffect(function () {
    setInternalSelection(selectedMethod);
  }, [selectedMethod]);
  var selectMethod = useCallback( /*#__PURE__*/function () {
    var _ref14 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(methodId, methodName, methodAmount) {
      var oldSelection;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              oldSelection = internalSelection;
              setInternalSelection(methodId);
              _context.prev = 2;
              _context.next = 5;
              return onSelectMethod(methodId, methodName, methodAmount);

            case 5:
              _context.next = 10;
              break;

            case 7:
              _context.prev = 7;
              _context.t0 = _context["catch"](2);
              setInternalSelection(oldSelection);

            case 10:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[2, 7]]);
    }));

    return function (_x, _x2, _x3) {
      return _ref14.apply(this, arguments);
    };
  }(), [internalSelection, onSelectMethod]);
  var availableShippingMethods = useMemo(function () {
    var _brandCheckout$availa;

    return (_brandCheckout$availa = brandCheckout === null || brandCheckout === void 0 ? void 0 : brandCheckout.availableShippingMethods) !== null && _brandCheckout$availa !== void 0 ? _brandCheckout$availa : [];
  }, [brandCheckout]);
  useEffect(function () {
    //default select the first shipping method if none selected to speed up checkout
    if (readyToSelect && !internalSelection && availableShippingMethods.length > 0) {
      var _method$price;

      var method = availableShippingMethods[0];
      selectMethod(method.id, method.name, (_method$price = method.price) === null || _method$price === void 0 ? void 0 : _method$price.amount);
    }
  }, [readyToSelect, availableShippingMethods, internalSelection, selectMethod]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500",
    as: "span"
  }, "Shipping", /*#__PURE__*/React.createElement(Box, {
    as: "span",
    color: "scheme.controlActive"
  }, "*")), readyToSelect ? (_brandCheckout$availa2 = brandCheckout.availableShippingMethods) !== null && _brandCheckout$availa2 !== void 0 && _brandCheckout$availa2.length ? /*#__PURE__*/React.createElement(RadioGroup, {
    value: internalSelection,
    onChange: function onChange(value) {
      var _methodObject$price;

      var methodObject = brandCheckout.availableShippingMethods.find(function (method) {
        return method.id === value;
      });
      selectMethod(value, methodObject === null || methodObject === void 0 ? void 0 : methodObject.name, methodObject === null || methodObject === void 0 ? void 0 : (_methodObject$price = methodObject.price) === null || _methodObject$price === void 0 ? void 0 : _methodObject$price.amount);
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    spacing: 4
  }, brandCheckout.availableShippingMethods.map(function (method) {
    return /*#__PURE__*/React.createElement(Radio, {
      key: method.id,
      value: method.id,
      isFullWidth: true
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      justifyContent: "space-between",
      align: "center"
    }, /*#__PURE__*/React.createElement(Text, null, method.name), /*#__PURE__*/React.createElement(Text, {
      fontWeight: "500"
    }, currencyFormatter.format(method.price.amount))));
  }))) : /*#__PURE__*/React.createElement(Text, null, "We're really sorry. Unfortunately, ".concat((_brandCheckout$brand$ = (_brandCheckout$brand = brandCheckout.brand) === null || _brandCheckout$brand === void 0 ? void 0 : _brandCheckout$brand.name) !== null && _brandCheckout$brand$ !== void 0 ? _brandCheckout$brand$ : 'this brand', " can't ship to your location.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SkeletonText, {
    noOfLines: 1,
    skeletonHeight: "1rem",
    spacing: "4",
    startColor: "scheme.border",
    endColor: "scheme.border"
  }), /*#__PURE__*/React.createElement(Text, {
    size: "sm"
  }, "Add shipping address to calculate shipping.")));
};
BrandCheckoutShippingMethod.propTypes = {
  brandCheckout: propTypes.shape({
    brand: propTypes.shape({
      name: propTypes.string
    }),
    availableShippingMethods: propTypes.arrayOf(propTypes.shape({
      id: propTypes.string,
      name: propTypes.string,
      price: propTypes.shape({
        amount: propTypes.number
      })
    }))
  }),
  onSelectMethod: propTypes.func,
  readyToSelect: propTypes.bool,
  selectedMethod: propTypes.string
};

export { AddressDetails, BrandCheckout, BrandCheckoutShippingMethod, OrderAddress, OrderPaymentMethod, OrderSummary, OrderSummaryTable, PaymentMethodDetails, ProductLine };
